<template>
    <div class="work-orders-expenses">
        <div class="work-orders-expenses__toolbar">
            <b-button
                class="align-baseline"
                title="Add New Row"
                variant="default"
                :disabled="!canEdit"
                @click="onAddNewItem"
            >
                <font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon>
            </b-button>
            <b-button
                class="align-baseline"
                title="Edit Selected Row"
                variant="default"
                :disabled="!isDeleteEditEnabled || !canEdit"
                @click="onItemEdit"
            >
                <font-awesome-icon :icon="['fas', 'pen-to-square']"></font-awesome-icon>
            </b-button>
            <b-button
                class="align-baseline"
                title="Delete Selected Row"
                variant="default"
                :disabled="!isDeleteEditEnabled || !canEdit"
                @click="onItemDelete"
            >
                <font-awesome-icon :icon="['fas', 'trash']"></font-awesome-icon>
            </b-button>
            <b-button class="align-baseline" title="Refresh" variant="default" @click="refresh">
                <font-awesome-icon :icon="['fas', 'arrows-rotate']"></font-awesome-icon>
            </b-button>
        </div>
        <grid
            class="work-orders-expenses__grid"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :isSearch="false"
            ref="grid"
            @selection-changed="onRowSelect"
            rowSelection="single"
        >
        </grid>
        <validation-observer v-slot="{ meta, handleSubmit, validate }" ref="form">
            <modal
                id="work-orders-expenses-edit-modal"
                ok-title="Save"
                :scrollable="false"
                :title="title"
                :okDisabled="invalid"
                :visible="isEditModalShown"
                @hide="onEditModalHide"
                @cancel="onEditModalHide"
                @ok.prevent="handleSubmit(_, onMaterialExpenseSave)"
                @shown="setFormValues"
            >
                <div>
                    {{ setInvalid(!meta.valid) }}
                    <b-row class="my-2">
                        <b-col md="2">
                            <label>Date:</label>
                        </b-col>
                        <b-col>
                            <datepicker class="mb-2" v-model="expenseDate" :display-format="userUnicodeDateFormat">
                            </datepicker>
                        </b-col>
                    </b-row>
                    <b-row class="my-2">
                        <b-col md="2">
                            <label>Description:</label>
                        </b-col>
                        <b-col>
                            <validation-provider
                                name="expenseDescription"
                                rules="noScript"
                                v-slot="{ errors, handleChange }"
                            >
                                <form-textarea
                                    v-model="expenseDescription"
                                    :error="errors[0]"
                                    :style="{ marginBottom: 0 }"
                                    @input="handleChange"
                                ></form-textarea>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="my-2">
                        <b-col md="2">
                            <label>Quantity:</label>
                        </b-col>
                        <b-col>
                            <validation-provider
                                name="expenseQuantity"
                                rules="required|validInteger"
                                v-slot="{ errors, handleChange }"
                            >
                                <form-input
                                    trim
                                    type="text"
                                    v-model="expenseQuantity"
                                    :style="{ marginBottom: 0 }"
                                    :error="errors[0]"
                                    @keypress="maskInputNumber($event)"
                                    @change="handleChange"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="my-2">
                        <b-col md="2">
                            <label>Price:</label>
                        </b-col>
                        <b-col>
                            <validation-provider
                                name="expensePrice"
                                rules="required|validFloat"
                                v-slot="{ errors, handleChange }"
                            >
                                <form-input
                                    trim
                                    type="text"
                                    v-model="expensePrice"
                                    :style="{ marginBottom: 0 }"
                                    :error="errors[0]"
                                    @keypress="maskInputNumber($event)"
                                    @change="handleChange"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                    </b-row>
                </div>
            </modal>
        </validation-observer>
        <modal
            id="work-orders-expenses-delete-modal"
            title="Delete"
            ok-title="Delete"
            @hide="isDeleteModalShown = false"
            @cancel="isDeleteModalShown = false"
            @ok="onMaterialExpenseDelete"
            :visible="isDeleteModalShown"
            >Are you sure you want to delete selected material expense?</modal
        >
    </div>
</template>

<script>

    import { mapGetters, mapActions } from 'vuex';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import { uuid, maskNumber } from '../../../../utils/objects';
    import API from './../../../../api';
    import Grid from '../../../generic/grid/grid.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import Datepicker from '../../../generic/datepicker/datepicker.vue';
    import FormTextarea from '../../../generic/form-textarea/form-textarea.vue';
    import FormInput from '../../../generic/form-input/form-input.vue';
    import FormInputGroup from '../../../generic/form-input-group/form-input-group.vue';
    import { WorkOrderActionType } from '../../../../constants';

    const MODAL_TIMEOUT = 200;

    export default {
        name: 'WorkOrdersMaterialExpenses',
        components: {
            Datepicker,
            Grid,
            Modal,
            ResponsiveButton,
            FormTextarea,
            FormInput,
            FormInputGroup,
        },
        inject: ['setGlobalInvalid'],
        props: {
            actionType: {
                type: String,
                default: WorkOrderActionType.Create,
            },
            canEdit: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                invalid: false,
                isEditMode: false,
                expenseDate: format(new Date(), 'yyyy-MM-dd'),
                expenseDescription: '',
                expenseQuantity: 0,
                expensePrice: 0,
                isDeleteModalShown: false,
                isEditModalShown: false,
                selectedItem: null,
                offset: 0,
                limit: 20,
                sort: 'number',
                search: '',
                columnDefs: [
                    {
                        field: 'date',
                        headerName: 'Date',
                        maxWidth: 120,
                        type: 'dateColumn',
                    },
                    {
                        field: 'description',
                        headerName: 'Description',
                        flex: 1,
                    },
                    {
                        field: 'qty',
                        headerName: 'Quantity',
                        flex: 0,
                    },
                    {
                        field: 'price',
                        headerName: 'Price',
                        maxWidth: 120,
                    },
                ],
                rowSelection: 'single',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            ...mapGetters('workOrders', ['localMaterialExpenses', 'workOrderToSave']),
            title() {
                return !!this.selectedItem && this.isEditMode ? 'Edit Material Expense' : 'Add Material Expense';
            },
            isDeleteEditEnabled() {
                return !!this.selectedItem;
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;

                    if (this.actionType === WorkOrderActionType.Create) {
                        return this.getLocalMaterialExpenses();
                    } else {
                        return API.get(`/app/work-orders/api/expenses-material/grid/`, {
                            params: {
                                work_order_pk: this.workOrderToSave.id,
                                offset,
                                limit,
                                sort,
                                desc,
                                search,
                            },
                        });
                    }
                };
            },
        },
        methods: {
            ...mapActions('workOrders', ['updateLocalMaterialExpenses']),
            maskInputNumber(evt) {
                return maskNumber(evt);
            },
            setInvalid(isInvalid) {
                this.invalid = isInvalid;
                this.setGlobalInvalid(isInvalid);
            },
            getLocalMaterialExpenses() {
                return Promise.resolve({
                    data: this.localMaterialExpenses,
                });
            },
            getEditModalValues() {
                return {
                    date: format(parseISO(this.expenseDate), 'yyyy-MM-dd'),
                    description: this.expenseDescription,
                    qty: this.expenseQuantity,
                    price: this.expensePrice,
                };
            },
            setEditModalValues(obj = {}) {
                this.expenseDate = obj.date || format(new Date(), 'yyyy-MM-dd');
                this.expenseDescription = obj.description || '';
                this.expenseQuantity = obj.qty || 0;
                this.expensePrice = obj.price || 0;
            },
            setFormValues() {
                this.$refs.form.setValues({
                    expenseDescription: this.expenseDescription,
                    expenseQuantity: this.expenseQuantity,
                    expensePrice: this.expensePrice,
                });
            },
            onEditModalHide() {
                this.isEditModalShown = false;
                const gridApi = this.$refs.grid.gridOptions.api;
                gridApi.deselectAll();
                setTimeout((_) => this.setEditModalValues(), MODAL_TIMEOUT);
            },
            onAddNewItem() {
                this.isEditMode = false;
                this.isEditModalShown = true;
            },
            onItemEdit() {
                this.isEditMode = true;
                this.isEditModalShown = true;
                this.setEditModalValues({
                    ...this.selectedItem,
                    date: format(parseISO(this.selectedItem.date), 'yyyy-MM-dd'),
                });
            },
            onItemDelete() {
                this.isDeleteModalShown = true;
            },
            onRowSelect() {
                const gridApi = this.$refs.grid.gridOptions.api;
                const selectedRows = gridApi.getSelectedRows();

                this.selectedItem = selectedRows.length ? selectedRows[0] : null;
            },
            onMaterialExpenseSave() {
                const materialExpense = this.getEditModalValues();

                (async () => {
                    if (this.isEditMode) {
                        this.editExpenseMaterial(materialExpense);
                    } else {
                        this.addExpenseMaterial(materialExpense);
                    }
                })();
                this.isEditModalShown = false;
            },
            onMaterialExpenseDelete() {
                (async () => {
                    this.deleteExpenseMaterial(this.selectedItem.id);
                })();
            },
            initGridSelectConfig() {
                const gridOptions = this.$refs.grid.gridOptions;
                gridOptions.rowSelection = 'single';
                gridOptions.api.addEventListener('selectionChanged', this.onRowSelect);
            },
            refresh() {
                this.$refs.grid.refreshDataSource();
            },
            async addExpenseMaterial(payload) {
                if (this.actionType === WorkOrderActionType.Create) {
                    this.dispatchLocalAction(() => {
                        payload.id = uuid();
                        this.updateLocalMaterialExpenses([...this.localMaterialExpenses, payload]);
                    });
                } else {
                    await this.dispatchRequest(
                        () =>
                            API.post(`/app/work-orders/api/expenses-material/`, {
                                ...payload,
                                work_order_pk: this.workOrderToSave.id,
                            }),
                        `Could not create material expense.`
                    );
                }
            },
            async editExpenseMaterial(payload) {
                if (this.actionType === WorkOrderActionType.Create) {
                    this.dispatchLocalAction(() => {
                        const expenseToEdit = this.localMaterialExpenses.find(
                            (expense) => expense.id === this.selectedItem.id
                        );

                        expenseToEdit.date = payload.date || expenseToEdit.date;
                        expenseToEdit.description = payload.description || expenseToEdit.description;
                        expenseToEdit.qty = payload.qty || expenseToEdit.qty;
                        expenseToEdit.price = payload.price || expenseToEdit.price;
                    });
                } else {
                    payload.date = format(parseISO(payload.date), 'yyyy-MM-dd');
                    await this.dispatchRequest(
                        () =>
                            API.patch(`/app/work-orders/api/expenses-material/${this.selectedItem.id}/`, {
                                ...payload,
                                work_order_pk: this.workOrderToSave.id,
                            }),
                        `Could not update material expense.`
                    );
                }
            },
            async deleteExpenseMaterial(id) {
                if (this.actionType === WorkOrderActionType.Create) {
                    this.dispatchLocalAction(() => {
                        const idx = this.localMaterialExpenses.findIndex(
                            (expense) => expense.id === this.selectedItem.id
                        );
                        const expenses = [
                            ...this.localMaterialExpenses.slice(0, idx),
                            ...this.localMaterialExpenses.slice(idx + 1),
                        ];
                        this.updateLocalMaterialExpenses(idx === -1 ? this.localMaterialExpenses : expenses);
                        this.selectedItem = null;
                    });
                } else {
                    await this.dispatchRequest(
                        () => API.delete(`/app/work-orders/api/expenses-material/${id}/`),
                        `Could not delete material expense.`
                    );
                    this.selectedItem = null;
                }
            },
            async dispatchLocalAction(action) {
                try {
                    action();
                } catch (e) {
                    console.log(e.message);
                } finally {
                    this.refresh();
                    this.$emit('materal-expenses-updated', [...this.localMaterialExpenses]);
                }
            },
            async dispatchRequest(request, errorMessage = '') {
                try {
                    await request();
                } catch {
                    this.errorToast({ text: errorMessage });
                } finally {
                    this.refresh();
                }
            },
        },
        mounted() {
            this.initGridSelectConfig();
        },
    };
</script>

<style lang="scss" scoped>
    $border: 1px solid #dee2e6;
    .work-orders-expenses {
        display: flex;
        height: 100%;

        &__toolbar {
            min-width: 30px;
            border-right: 1px solid #dee2e6;
            margin-right: 5px;
            display: flex;
            flex-direction: column;
            padding-right: 5px;

            > button {
                margin: 2px;
            }
        }

        &__grid {
            width: 100%;
        }
    }

    @media (max-width: 770px) {
        .work-orders-expenses {
            &__toolbar {
                > button {
                    padding: 0.1rem 0.37rem;
                    font-size: 0.7rem;
                }
            }
        }
    }
</style>
